import './CreateEventModal.scss';
import { memo, ReactNode, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { MdAdd } from 'react-icons/md';
import { Modal } from 'react-bootstrap';
import { useSelector as useFormSelector } from '@tanstack/react-form';
import { EventType, SignalTag, SourceKind } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { BandConfig } from '@blisspointmedia/bpm-types/dist/OracleApi';
import { App2Channels } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { schemaValidators } from '@Components/Forms/form-field-utils';
import {
  AppFieldOf,
  DateField,
  SingleDropdownField,
  StaticField,
  TextField
} from '@Components/Forms/FormField';
import { useAppForm } from '@Components/Forms/useAppForm';
import { IconButton } from '@Components/IconButton';
import { Appearance, Design, Size, StretchMode } from '@Components/designConstants';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { useSetError } from '@src/redux/modals';
import { companiesSelector } from '@src/redux/user';
import { getSignalLabel } from '../Detail/activity-feed-detail-utils';
import {
  THEME_OPTIONS,
  CHANNEL_OPTIONS,
  EVENT_TYPE_OPTIONS,
  SOURCE_OPTIONS
} from '../Filters/activity-feed-filters-constants';
import { useCreateEvent } from '../useOracleEventMutations';
import { soleClient } from '../activity-feed-utils';
import {
  createEventSchema,
  emptyCreateEventValues,
  toCreateEventBody
} from '../activity-feed-form-utils';
import { useOracleCidScope } from '../useOracleCidScope';
import { useOracleEventCounts } from '../useOracleEventCounts';
import { EventTheme } from '../oracle-taxonomy-constants';
import { deriveEventFields } from '../oracle-taxonomy-utils';

const SIGNAL_TAG_OPTIONS = Object.values(SignalTag).map(st => ({
  label: getSignalLabel(st),
  value: st
}));

export const CreateEventModal = memo((): ReactNode => {
  const [show, setShow] = useState(false);
  const counts = useOracleEventCounts();

  if (!counts) return null;

  const close = () => setShow(false);

  return (
    <>
      <IconButton onClick={() => setShow(true)} size={Size.MD}>
        <MdAdd />
      </IconButton>
      {/* The modal unmounts its content on hide, so each open starts a fresh form. */}
      <Modal show={show} onHide={close} className='create-event-modal' centered size='lg'>
        <CreateEventForm bands={counts.bands} onClose={close} />
      </Modal>
    </>
  );
});
CreateEventModal.displayName = 'CreateEventModal';

interface CreateEventFormProps {
  bands: BandConfig;
  onClose: () => void;
}

const CreateEventForm = ({ bands, onClose }: CreateEventFormProps): ReactNode => {
  const { cid: scopedCid, selectedCids } = useOracleCidScope();
  const companies = useSelector(companiesSelector);
  // Limit to the clients the feed is showing, so a new event lands in the current view.
  const clientCids = useMemo(
    () => (selectedCids.length > 0 ? selectedCids : companies.map(c => c.company)),
    [selectedCids, companies]
  );
  const clientOptions = useMemo(
    () => clientCids.map(cid => ({ label: cid, value: cid })),
    [clientCids]
  );

  const { mutateAsync: submitCreate } = useCreateEvent();
  const setError = useSetError();

  const form = useAppForm({
    defaultValues: emptyCreateEventValues(scopedCid || soleClient(clientCids)),
    validators: schemaValidators(createEventSchema),
    onSubmit: async ({ value }) => {
      try {
        const submission = createEventSchema.parse(value);
        await submitCreate(
          toCreateEventBody(submission, deriveEventFields(submission.eventType, bands))
        );
        Mixpanel.track(MxE.RecordCreated, { page: PAGE_NAMES.ACTIVITY_FEED, entity: 'event' });
        onClose();
      } catch (e) {
        setError({ message: `Couldn't create event: ${e.message}`, reportError: e });
      }
    }
  });

  const eventType = useFormSelector(form.store, state => state.values.eventType);
  const derived = eventType ? deriveEventFields(eventType, bands) : null;

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Create New Event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!scopedCid && (
          <AppFieldOf
            form={form}
            name='cid'
            field={SingleDropdownField<string>}
            fieldProps={{
              searchable: true,
              label: 'Client',
              required: true,
              fieldClassName: 'field-group',
              options: clientOptions,
              placeholder: 'Select client',
              fitMenuToContent: true
            }}
          />
        )}
        <AppFieldOf
          form={form}
          name='title'
          field={TextField}
          fieldProps={{
            label: 'Title',
            required: true,
            fieldClassName: 'field-group',
            placeholder: 'Enter a short headline'
          }}
        />
        <div className='field-row'>
          <AppFieldOf
            form={form}
            name='date'
            field={DateField}
            fieldProps={{
              label: 'Date',
              required: true,
              fieldClassName: 'field-group',
              appearance: Appearance.OUTLINED,
              design: Design.NEUTRAL,
              size: Size.LG,
              stretch: StretchMode.FILL
            }}
          />
          <AppFieldOf
            form={form}
            name='time'
            field={TextField}
            fieldProps={{ label: 'Time', type: 'time', fieldClassName: 'field-group' }}
          />
        </div>
        <AppFieldOf
          form={form}
          name='eventType'
          field={SingleDropdownField<EventType>}
          fieldProps={{
            searchable: true,
            label: 'Event Type',
            required: true,
            fieldClassName: 'field-group',
            options: EVENT_TYPE_OPTIONS,
            placeholder: 'Select event type'
          }}
        />
        <div className='field-row'>
          <StaticField label='Theme' fieldClassName='field-group'>
            <Dropdown<EventTheme>
              searchable
              disabled
              value={derived?.theme ?? null}
              onChange={() => {}}
              options={THEME_OPTIONS}
              placeholder='Derived from event type'
            />
          </StaticField>
          <StaticField label='Signal Tag' fieldClassName='field-group'>
            <Dropdown<SignalTag>
              searchable
              disabled
              value={derived?.signalTag ?? null}
              onChange={() => {}}
              options={SIGNAL_TAG_OPTIONS}
              placeholder='Derived from event type'
            />
          </StaticField>
        </div>
        <AppFieldOf
          form={form}
          name='details'
          field={TextField}
          fieldProps={{
            label: 'Details',
            multiline: true,
            rows: 3,
            fieldClassName: 'field-group',
            placeholder: 'What happened, briefly'
          }}
        />
        <AppFieldOf
          form={form}
          name='recommendedAction'
          field={TextField}
          fieldProps={{
            label: 'Recommended Action',
            fieldClassName: 'field-group',
            placeholder: 'Suggested next step'
          }}
        />
        <div className='field-row'>
          <AppFieldOf
            form={form}
            name='source'
            field={SingleDropdownField<SourceKind>}
            fieldProps={{
              searchable: true,
              label: 'Source',
              fieldClassName: 'field-group',
              options: SOURCE_OPTIONS,
              placeholder: 'Select source',
              reactSelectProps: { menuPlacement: 'top' }
            }}
          />
          <AppFieldOf
            form={form}
            name='channel'
            field={SingleDropdownField<App2Channels>}
            fieldProps={{
              searchable: true,
              label: 'Channel',
              fieldClassName: 'field-group',
              options: CHANNEL_OPTIONS,
              placeholder: 'Select channel',
              reactSelectProps: { menuPlacement: 'top' }
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onClose}>
          Cancel
        </Button>
        <form.AppForm>
          <form.SubmitButton>Create Event</form.SubmitButton>
        </form.AppForm>
      </Modal.Footer>
    </>
  );
};
